<template>
    <div>
        <SimpleTable
            v-if="plazos && plazos.length > 0"
            :columns="columns"
            :data="plazos"
            :row-class="getRowClass"
            @row-click="onRowClick"
            :default-col-width="defaultColWidth"
        >
            <template #cell-pago="{ row: plazo }">
                <div class="tw:flex tw:justify-center" @click.stop >
                    <ToggleSwitch
                        v-if="isPagable(plazo.estado)"
                        :value="isLocallySelected(plazo.id)"
                        size="sm"
                        variant="alt"
                        :aria-label="`Seleccionar plazo ${plazo.fecha || plazo.id}`"
                        @input="onCheckboxChange(plazo)"
                    />
                    <span v-else class="tw:w-4 tw:h-4 tw:inline-block"></span>
                </div>
            </template>

            <template #cell-fecha="{ row: plazo }">
                <span class="tw:text-slate-700 tw:font-medium">
                    {{ formatearFecha(plazo.fecha) || plazo.fecha || '—' }}
                </span>
            </template>

            <template #cell-importe="{ row: plazo }">
                <template v-if="plazo.descuento_aplicado > 0">
                    <span class="tw:line-through tw:text-slate-400 tw:font-normal tw:mr-1">
                        {{ formatearPrecio(importeOriginal(plazo)) }}
                    </span>
                    {{ formatearPrecio(plazo.importe_con_gd || plazo.importe) }}
                </template>
                <template v-else>
                    {{ formatearPrecio(plazo.importe_con_gd || plazo.importe) }}
                </template>
            </template>
    
            <template #cell-estado="{ row: plazo }">
                <div class="tw:flex tw:justify-end">
                    <BadgeFront
                        :variant="estadoVariant(plazo.estado)"
                        :label="estadoI18nKey(plazo.estado) ? $t(estadoI18nKey(plazo.estado)) : (plazo.estado_string || '—')"
                        :dot="true"
                        :icon="null"
                    />
                </div>
            </template>
        </SimpleTable>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import { usePlazos } from '@/composables/front/perfil/usePlazos';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import ToggleSwitch from '@/modules/front/ui/ToggleSwitch.vue';
    import SimpleTable from '@/modules/front/ui/SimpleTable.vue';

    const props = defineProps({
        plazos: {
            type: Array,
            default: () => [],
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        defaultColWidth: {
            type: String,
            default: '140px'
        },
    });

    const emit = defineEmits(['plazo-toggled']);

    const {
        formatearPrecio,
        formatearFecha,
        isPagable,
        importeOriginal,
        estadoVariant,
        estadoI18nKey,
    } = usePlazos();

    // ── Estado local de selección ──
    const localSelectedIds = ref(new Set());

    // computed reactivo: Map<id, boolean> para que el template reaccione
    const selectedMap = computed(() => {
        const map = {};
        localSelectedIds.value.forEach(id => { map[id] = true; });
        return map;
    });

    const isLocallySelected = plazoId => !!selectedMap.value[plazoId];

    const toggleLocalSelection = plazoId => {
        const next = new Set(localSelectedIds.value);
        if (next.has(plazoId)) {
            next.delete(plazoId);
        } else {
            next.add(plazoId);
        }
        localSelectedIds.value = next;
    };

    const onCheckboxChange = plazo => {
        if (!isPagable(plazo.estado)) return;
        toggleLocalSelection(plazo.id);
        emit('plazo-toggled', plazo);
    };

    const columns = computed(() => {
        const cols = [
            { key: 'fecha',   label: t('perfil.fecha_cobro'), align: 'left' },
            { key: 'importe', label: t('perfil.importe'),     align: 'center' },
            { key: 'estado',  label: t('perfil.estado'),      align: 'right' },
        ];
        if (props.selectable) {
            cols.push({ key: 'pago', label: t('perfil.pago'), align: 'center' });
        }
        return cols;
    });

    const getRowClass = plazo => {
        if (!props.selectable) return '';
        const selected = isLocallySelected(plazo.id);
        const pagable = isPagable(plazo.estado);
        const classes = ['tw:transition-all tw:duration-150 tw:border-l-4'];
        if (pagable) {
            classes.push('tw:cursor-pointer');
        } else {
            classes.push('tw:opacity-70 tw:border-l-transparent');
        }
        if (selected) {
            classes.push('tw:bg-blue-50 tw:border-l-blue-600');
        } else {
            classes.push('tw:bg-white tw:border-l-transparent');
        }
        return classes.join(' ');
    };

    const onRowClick = plazo => {
        if (!props.selectable || !isPagable(plazo.estado)) return;
        toggleLocalSelection(plazo.id);
        emit('plazo-toggled', plazo);
    };
</script>
